import { twMerge } from 'tailwind-merge';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TranscriptionExtended } from 'flow/types/CallRecord';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import { PageType } from 'flow/components/call-history-v2/types';

interface Props {
    callRecordKey: CallRecordKey;
    extendedTranscription?: TranscriptionExtended;
    plainTranscript?: string;
    plainSummary?: string;
    isGenerating?: boolean;
    pageType?: PageType;
    onClickShowTranscript?: () => void;
}

const TranscriptButton = ({
    callRecordKey,
    extendedTranscription,
    plainTranscript,
    plainSummary,
    isGenerating,
    pageType,
    onClickShowTranscript,
}: Props) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );

    const hasViewableContent =
        !!extendedTranscription?.rawTranscript ||
        !!extendedTranscription?.summary ||
        !!plainTranscript ||
        !!callRecord?.getIn(['voiceMessage', 'transcript']);
    const buttonIsDisabled = isGenerating || !hasViewableContent;

    const getTooltipLabel = () => {
        if (isGenerating) return i18next.t('ai.transcript.generating');
        if (!hasViewableContent)
            return i18next.t('ai.transcript.no_data_available');
        return undefined;
    };

    const handleClick = () => {
        if (buttonIsDisabled || !callRecord) return;
        onClickShowTranscript?.();
        openModal('transcription', {
            callRecord,
            transcriptionExtended: extendedTranscription,
            plainTranscript:
                plainTranscript ||
                callRecord.getIn(['voiceMessage', 'transcript']),
            plainSummary,
            pageType,
        });
    };

    return (
        <IconButton
            onClick={handleClick}
            disabled={buttonIsDisabled}
            tooltipLabel={getTooltipLabel()}
            className={twMerge(
                'flex h-10 shrink-0 items-center gap-1 rounded bg-gray-200 px-2 transition-all duration-200',
                buttonIsDisabled && 'opacity-40',
            )}
        >
            <Icon iconKey="article" size="1.25rem" className="text-gray-600" />
            <span className="text-xs text-gray-800 antialiased">
                {i18next.t('ai.transcript.show_full_transcript')}
            </span>
        </IconButton>
    );
};

export default TranscriptButton;
